import {isNil} from 'lodash';
import {useMemo, useState, useCallback, useEffect} from 'react';
import {PropTypes} from 'prop-types';
import {FmlxMenu, FmlxIcon} from 'fmlx-common-ui';
import {Size} from '../../constants/UIComponentEnum';

const createVirtualDom = (x, y) => ({
  getBoundingClientRect: () => ({
    width: 0,
    height: 0,
    top: y,
    right: x,
    bottom: y,
    left: x,
  }),
  nodeType: 1,
});

function FastContextMenu({open, contextMenuItems, elementsRef, attributeIdentifier, onContextMenu, onClose}) {
  const [virtualAnchorElement, setVirtualAnchorElement] = useState();

  const isControlled = !isNil(open);

  const listItem = useMemo(
    () =>
      contextMenuItems.map((item) => ({
        ...item,
        icon: typeof item.icon === 'string' ? <FmlxIcon size={Size.XS} name={item.icon} /> : item.icon,
        onClick: () => {
          setVirtualAnchorElement(null);
          item.onClick();
        },
      })),
    [contextMenuItems],
  );

  const handleClose = useCallback(() => {
    if (!virtualAnchorElement) return;

    setVirtualAnchorElement(null);
    onClose();
  }, [virtualAnchorElement, onClose]);

  const handleClickCtxMenu = useCallback(
    async (evt) => {
      const isNotRightClick = evt.button !== 2;
      const {target} = evt || {};

      // click's not valid
      if (!target || isNotRightClick) {
        handleClose();
        return;
      }

      const clickedContextMenuElement = elementsRef.current.find((ele) => ele?.contains(target));

      if (!clickedContextMenuElement) {
        handleClose();
        return;
      }

      // get the clicked element's attribute value
      const itemValue = clickedContextMenuElement?.attributes[attributeIdentifier]?.value;
      // handler can override opening the context menu by returning true/false
      onContextMenu(clickedContextMenuElement, itemValue);

      // set context menu location
      const clickedCoordinates = {
        left: evt.pageX || evt.screenX,
        top: evt.pageY || evt.screenY,
      };

      const vDom = createVirtualDom(clickedCoordinates.left, clickedCoordinates.top);
      setVirtualAnchorElement(vDom);
    },
    [elementsRef, attributeIdentifier, onContextMenu, handleClose],
  );

  useEffect(() => {
    const root = document.getElementById('root');

    root.addEventListener('mousedown', handleClickCtxMenu);
    root.addEventListener('wheel', handleClose);
    return () => {
      root.removeEventListener('mousedown', handleClickCtxMenu);
      root.removeEventListener('wheel', handleClose);
    };
  }, [handleClickCtxMenu, handleClose]);

  const getIsOpenState = () => (isControlled ? open : Boolean(virtualAnchorElement) && listItem?.length > 0);

  return (
    <FmlxMenu
      onClose={handleClose}
      open={getIsOpenState()}
      listItem={listItem}
      anchorEl={virtualAnchorElement}
      anchorReference="anchorEl"
    />
  );
}

FastContextMenu.propTypes = {
  contextMenuItems: PropTypes.object.isRequired,
  elementsRef: PropTypes.object,
  attributeIdentifier: PropTypes.string,
  onContextMenu: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

FastContextMenu.defaultProps = {
  elementsRef: [],
  attributeIdentifier: 'data-id',
  onContextMenu: () => {},
  onClose: () => {},
  open: undefined,
};

export default FastContextMenu;
